import { Modal, Typography, Form, Input, Button, Select, App } from "antd";
import { useSelector } from "react-redux";
import config from "../../config";
import { useEffect, useState, useTransition } from "react";

const LoyaltyCouponsModal = ({
  open,
  mode = "add",
  onClose,
  initialValues = {},
  refetch = () => {},
  onCheckCouponAlreadyExist = () => {},
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const shopDetails = useSelector((state) => state.shopDetails);
  const productsForSearch = useSelector((state) => state.productsForSearch);
  const [loading, setLoading] = useState(false);
  const couponMutation = async (payload) => {
    try {
      const response = await fetch(config.newApiGateway + "/loyalty/loyalty", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": config.newApiKey,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data.status === "SUCCESS") {
        refetch();
        onClose();
        form.resetFields();
        message.success(data.message);
      }
      if (data.status === "FAILURE") {
        message.success(data.message);
      }

      console.log("couponMutation", data);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    await couponMutation({
      action: mode === "edit" ? "updateCoupon" : "addCoupon",
      shopId: shopDetails.shopId,
      couponAddedby: shopDetails.phoneNumber,
      couponDetails: values.couponDetails,
      minLoyaltyPointsReq: values.minLoyaltyPointsReq,
      pincode: shopDetails.userPincode,
      shopType: shopDetails.shopType,
      userId10Digit: shopDetails.userId10Digit,
      rewardType: {
        name: values.rewardType,
        value: values.rewardType === "product" ? values.productId : values.cash,
      },
      ...(mode === "edit" && { couponId: initialValues.couponId }),
      ...(["Outlet", "Employee"].includes(shopDetails.userType) && {
        parentShopId: shopDetails.ownerShopId,
      }),
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    form.setFieldsValue({
      ...initialValues,
      rewardType: initialValues.rewardType?.name,
      ...(initialValues.rewardType?.name === "product" && {
        productId: productsForSearch.find(
          (i) => i.id === initialValues.rewardType?.value
        )?.id,
      }),
      ...(initialValues.rewardType?.name === "cash" && {
        cash: initialValues.rewardType?.value,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, productsForSearch]);

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={false}
    >
      <Typography.Title level={3}>
        {mode === "add" ? "Add coupon" : "Update coupon"}
      </Typography.Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="couponDetails"
          label="Coupon Name"
          rules={[
            { required: true, message: "Please enter a coupon Name" },
            {
              validator: (_, value) => {
                const isCouponNameExist = onCheckCouponAlreadyExist(
                  value,
                  initialValues.couponId
                );
                if (isCouponNameExist) {
                  return Promise.reject(
                    new Error(
                      "Coupon Name already exists. Please enter a different name"
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="Enter the coupon name" />
        </Form.Item>
        <Form.Item
          name="rewardType"
          label="Reward Type"
          rules={[{ required: true, message: "Select a coupon type" }]}
        >
          <Select>
            <Select.Option value="product">Product</Select.Option>
            <Select.Option value="cash">Cash</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.rewardType !== currentValues.rewardType
          }
          dependencies={["rewardType"]}
        >
          {({ getFieldValue }) => {
            return getFieldValue("rewardType") === "product" ? (
              <Form.Item
                name="productId"
                label="Product"
                rules={[{ required: true, message: "Select a product" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Product to be given"
                  filterOption={(input, option) => {
                    return (option?.label ?? "")
                      ?.toLowerCase?.()
                      ?.includes(input?.toLowerCase?.());
                  }}
                  notFoundContent="No products found"
                >
                  {productsForSearch.map((product) => (
                    <Select.Option
                      key={product.id}
                      label={product.name}
                      value={product.id}
                    >
                      {product.name}
                      {product.color && (
                        <span
                          style={{
                            height: "20px",
                            width: "20px",
                            borderRadius: "50%",
                            backgroundColor: product.color,
                            display: "inline-block",
                            marginLeft: "0.5rem",
                            marginRight: "0.5rem",
                            verticalAlign: "middle",
                          }}
                        />
                      )}
                      {product.size && <span> (Size - {product.size})</span>}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.rewardType !== currentValues.rewardType
          }
          dependencies={[""]}
        >
          {({ getFieldValue }) => {
            return getFieldValue("rewardType") === "cash" ? (
              <Form.Item
                name="cash"
                label="Cash"
                rules={[
                  { required: true, message: "Please enter the cash amount" },
                  {
                    pattern: /^[0-9]+$/,
                    message: "Please enter a valid cash amount",
                  },
                ]}
              >
                <Input placeholder="Enter the cash amount to be given" />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
        <Form.Item
          name="minLoyaltyPointsReq"
          label="Loyalty Point"
          rules={[
            { required: true, message: "Please enter a loyalty point" },
            {
              pattern: /^[0-9]+$/,
              message: "Please enter a valid loyalty point",
            },
          ]}
        >
          <Input placeholder="Enter the required loyalty point" />
        </Form.Item>
        <Form.Item>
          <div
            style={{
              textAlign: "right",
            }}
          >
            <Button loading={loading} type="primary" htmlType="submit">
              Submit
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LoyaltyCouponsModal;
